import { FC, useState, useEffect } from 'react'
import { useAuth } from '../../auth'
import { getVesselQuotationFeed, vesselSelectQuotation, vesselRejectQuotation } from '../core/_requests'
import { toast } from 'react-toastify'
import 'react-toastify/dist/ReactToastify.css'

interface QuotationFeed {
  id: number
  totalAmount?: number
  currency?: string
  status?: string
  vendor?: {
    name?: string
  }
  rfq?: {
    id?: number
    title?: string
  }
  sentToVesselAt?: string | null
}

const VESSEL_VISIBLE_STATUSES = new Set(['SENT_TO_VESSEL', 'SENT_TO_VESSEL_FOR_SELECTION'])

const CrewQuotationFeed: FC = () => {
  const { currentUser } = useAuth()
  const [quotations, setQuotations] = useState<QuotationFeed[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    loadQuotations()
  }, [])

  const loadQuotations = async () => {
    try {
      setLoading(true)
      setError(null)
      const data = await getVesselQuotationFeed()
      const scoped = (data || []).filter((quotation: any) =>
        VESSEL_VISIBLE_STATUSES.has(String(quotation?.status || '').toUpperCase())
      )
      setQuotations(scoped)
    } catch (err: any) {
      const errorMessage = 'Failed to load quotations'
      setError(errorMessage)
      toast.error(errorMessage)
      console.error('Error loading vessel quotation feed:', err)
    } finally {
      setLoading(false)
    }
  }

  const handleVesselSelect = async (quotationId: number) => {
    try {
      const remark = window.prompt('Optional remark for selection?') || undefined
      await vesselSelectQuotation(quotationId, remark)
      toast.success('Quotation selected successfully')
      await loadQuotations()
    } catch (err: any) {
      toast.error('Failed to select quotation')
    }
  }

  const handleVesselReject = async (quotationId: number) => {
    try {
      const remark = window.prompt('Optional remark for rejection?') || undefined
      await vesselRejectQuotation(quotationId, remark)
      toast.success('Quotation rejected successfully')
      await loadQuotations()
    } catch (err: any) {
      toast.error('Failed to reject quotation')
    }
  }

  const formatAmount = (amount?: number, currency?: string) => {
    if (!amount) return '-'
    const cur = currency || 'USD'
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: cur,
    }).format(amount)
  }

  const formatDate = (dateString?: string | null) => {
    if (!dateString) return '-'
    try {
      return new Date(dateString).toLocaleDateString()
    } catch {
      return dateString
    }
  }

  if (loading) {
    return (
      <div className='card'>
        <div className='card-header'>
          <h3 className='card-title'>Quotations for Selection</h3>
        </div>
        <div className='card-body'>
          <div className='text-center py-10'>
            <div className='spinner-border' role='status'>
              <span className='visually-hidden'>Loading...</span>
            </div>
            <p className='text-muted mt-3'>Loading quotations...</p>
          </div>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className='card'>
        <div className='card-header'>
          <h3 className='card-title'>Quotations for Selection</h3>
        </div>
        <div className='card-body'>
          <div className='alert alert-danger' role='alert'>
            <h4 className='alert-heading'>Error Loading Quotations</h4>
            <p>{error}</p>
            <button className='btn btn-sm btn-danger mt-3' onClick={loadQuotations}>
              Retry
            </button>
          </div>
        </div>
      </div>
    )
  }

  if (quotations.length === 0) {
    return (
      <div className='card'>
        <div className='card-header'>
          <h3 className='card-title'>Quotations for Selection</h3>
        </div>
        <div className='card-body'>
          <div className='text-center py-10'>
            <p className='text-muted'>No quotations have been sent to your vessel for selection</p>
            <button className='btn btn-sm btn-primary mt-3' onClick={loadQuotations}>
              Refresh
            </button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className='card'>
      <div className='card-header'>
        <h3 className='card-title'>Quotations Sent for Selection ({quotations.length})</h3>
        <button className='btn btn-sm btn-light' onClick={loadQuotations}>
          Refresh
        </button>
      </div>
      <div className='card-body'>
        <div className='table-responsive'>
          <table className='table table-row-bordered table-row-hover gy-4 gs-7'>
            <thead>
              <tr className='fw-bold fs-6 text-gray-800 border-bottom-2'>
                <th>RFQ</th>
                <th>Vendor</th>
                <th>Amount</th>
                <th>Sent Date</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {quotations.map((quotation) => (
                <tr key={quotation.id}>
                  <td>
                    <span className='fw-bold'>
                      {quotation.rfq?.title || `RFQ #${quotation.rfq?.id || 'N/A'}`}
                    </span>
                  </td>
                  <td>{quotation.vendor?.name || 'Unknown Vendor'}</td>
                  <td>{formatAmount(quotation.totalAmount, quotation.currency)}</td>
                  <td>{formatDate(quotation.sentToVesselAt)}</td>
                  <td>
                    <span className='badge bg-primary'>
                      {String(quotation.status || 'PENDING').replace(/_/g, ' ')}
                    </span>
                  </td>
                  <td>
                    <button
                      className='btn btn-sm btn-success me-2'
                      onClick={() => handleVesselSelect(quotation.id)}
                    >
                      Select
                    </button>
                    <button
                      className='btn btn-sm btn-danger'
                      onClick={() => handleVesselReject(quotation.id)}
                    >
                      Reject
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}

export default CrewQuotationFeed
